import { Checkmark, Subtract } from "@carbon/icons-react";
import cx from "classnames";
import type { ForwardRefRenderFunction, HTMLAttributes } from "react";
import { forwardRef, useRef } from "react";
import {
	type AriaCheckboxProps,
	VisuallyHidden,
	mergeProps,
	useCheckbox,
	useFocusRing,
} from "react-aria";
import { useToggleState } from "react-stately";

import styles from "./_.module.css";

export type InputCheckboxProps = AriaCheckboxProps & {
	className?: string;
	size?: "sm" | "md" | "lg";
};

const BaseInputCheckbox: ForwardRefRenderFunction<
	HTMLInputElement,
	InputCheckboxProps & Omit<HTMLAttributes<HTMLButtonElement>, "onChange">
> = ({ className, size = "md", ...props }, ref) => {
	// Hooks
	const state = useToggleState(props);
	const internalRef = useRef<HTMLInputElement | null>(null);
	const { inputProps } = useCheckbox(props, state, internalRef);
	const { focusProps } = useFocusRing();

	// Variables
	const isSelected = state.isSelected || props.isIndeterminate;
	const checkColor = isSelected ? "text-inverse-primary fill-icon-inverse-default" : "opacity-0";
	const focusPrimaryClassList =
		"ring-default focus:outline-none focus:border-brand focus:ring-4 focus:shadow-none";

	return (
		<label
			className={cx(
				"flex items-center gap-2 cursor-pointer",
				"text-primary",
				size === "sm" && "text-sm",
				size === "md" && "text-md",
				size === "lg" && "text-lg"
			)}
		>
			<VisuallyHidden>
				<input
					{...mergeProps(inputProps, focusProps)}
					ref={(el) => {
						internalRef.current = ref !== null && "current" in ref ? ref.current : el;
					}}
				/>
			</VisuallyHidden>
			<button
				type="button"
				aria-label={props["aria-label"] ?? "Select option"}
				className={cx(
					"rounded",
					focusPrimaryClassList,
					className,
					styles["inputcheckbox"],
					isSelected && styles["inputcheckbox--checked"],
					size === "sm" && styles["inputcheckbox--sm"],
					size === "md" && styles["inputcheckbox--md"],
					size === "lg" && styles["inputcheckbox--lg"]
				)}
				onClick={() => props.onChange?.(!isSelected)}
				disabled={props.isDisabled}
			>
				{props.isIndeterminate ? (
					<Subtract className={checkColor} />
				) : (
					<Checkmark className={checkColor} />
				)}
			</button>
			{props.children}
		</label>
	);
};

export const InputCheckbox = forwardRef(BaseInputCheckbox);
